<template>
  <div class="maintenance-mode">
    <!-- Header -->
    <v-app-bar
      :color="$vuetify.theme.isDark ? 'background-dark' : 'white'"
      flat
      class="maintenance-header"
      app
    >
      <v-container class="d-flex align-center justify-space-between pa-0">
        <div class="d-flex align-center">
          <v-img src="/img/app_name.png" width="100" class="mb-3"></v-img>
        </div>
        <div class="d-flex align-center">
          <span class="maintenance-badge">
            <span class="badge-dot"></span>
            Scheduled Maintenance
          </span>
        </div>
      </v-container>
    </v-app-bar>

    <v-main
      :class="$vuetify.theme.isDark ? 'background-dark' : 'background-light'"
      class="d-flex align-center"
    >
      <v-container>
        <v-row justify="center">
          <v-col cols="12" md="10" lg="8">
            <div class="text-center">
              <!-- Illustration -->
              <div class="mb-8">
                <v-img
                  src="/img/system-upgrade.png"
                  alt="System upgrade illustration"
                  max-width="256"
                  class="mx-auto"
                  style="object-fit: contain"
                ></v-img>
              </div>

              <!-- Header -->
              <h1
                class="text-h4 text-md-h3 text-lg-h2 font-weight-bold mb-4"
                :class="$vuetify.theme.isDark ? 'white--text' : 'text--primary'"
              >
                {{ title || "We'll Be Back Soon" }}
              </h1>

              <!-- Description -->
              <p
                class="text-body-1 text-md-h6 mb-10 mx-auto"
                style="max-width: 600px; line-height: 1.7"
                :class="
                  $vuetify.theme.isDark
                    ? 'grey--text text--lighten-1'
                    : 'grey--text text--darken-1'
                "
              >
                {{
                  message ||
                  "We're performing scheduled maintenance to improve your experience. Our team is working hard to bring you new features and improvements."
                }}
              </p>

              <!-- Countdown Timer -->
              <div class="mb-12">
                <div class="d-flex align-center justify-center mb-6">
                  <v-icon
                    size="20"
                    class="mr-2"
                    :class="
                      $vuetify.theme.isDark
                        ? 'grey--text text--lighten-1'
                        : 'grey--text text--darken-1'
                    "
                  >
                    mdi-clock-outline
                  </v-icon>
                  <span
                    class="text-caption font-weight-medium text-uppercase letter-spacing"
                    :class="
                      $vuetify.theme.isDark
                        ? 'grey--text text--lighten-1'
                        : 'grey--text text--darken-1'
                    "
                  >
                    Estimated time remaining
                  </span>
                </div>
                <div
                  class="d-flex align-center justify-center flex-wrap countdown-wrapper"
                >
                  <div
                    v-for="(item, index) in countdownItems"
                    :key="index"
                    class="d-flex align-center"
                  >
                    <div class="d-flex flex-column align-center">
                      <div class="countdown-card mb-2 orange">
                        <span class="countdown-number white--text">
                          {{ item.value }}
                        </span>
                      </div>
                      <span
                        class="countdown-label text-caption font-weight-bold text-uppercase"
                      >
                        {{ item.label }}
                      </span>
                    </div>
                    <span
                      v-if="index < countdownItems.length - 1"
                      class="countdown-separator mx-2"
                      :class="
                        $vuetify.theme.isDark
                          ? 'grey--text text--lighten-1'
                          : 'grey--text text--darken-1'
                      "
                    >
                      :
                    </span>
                  </div>
                </div>
              </div>

              <!-- Info Card -->
              <div class="d-flex justify-center mb-10">
                <div class="info-card-wrapper">
                  <v-card
                    flat
                    class="text-left pa-5"
                    :class="$vuetify.theme.isDark ? 'grey darken-3' : 'white'"
                  >
                    <div class="d-flex align-center mb-3">
                      <div
                        class="icon-container mr-3 accent--text"
                        :class="
                          $vuetify.theme.isDark
                            ? 'accent lighten-4'
                            : 'accent lighten-5'
                        "
                      >
                        <v-icon size="24">mdi-email-outline</v-icon>
                      </div>
                      <h3
                        class="font-weight-semibold mb-0"
                        :class="
                          $vuetify.theme.isDark
                            ? 'white--text'
                            : 'text--primary'
                        "
                      >
                        Need help?
                      </h3>
                    </div>
                    <p
                      class="text-body-2 mb-0"
                      style="line-height: 1.7"
                      :class="
                        $vuetify.theme.isDark
                          ? 'grey--text text--lighten-1'
                          : 'grey--text text--darken-1'
                      "
                    >
                      Contact our support team at
                      <a
                        href="mailto:support@built.africa"
                        class="text-decoration-none"
                        :class="
                          $vuetify.theme.isDark
                            ? 'primary--text'
                            : 'primary--text'
                        "
                        @click.prevent="contactSupport"
                      >
                        support@built.africa
                      </a>
                      for urgent matters.
                    </p>
                  </v-card>
                </div>
              </div>

              <!-- Footer Message -->
              <p
                class="text-caption mb-0"
                :class="
                  $vuetify.theme.isDark
                    ? 'grey--text text--lighten-2'
                    : 'grey--text text--darken-1'
                "
              >
                Thank you for your patience. We appreciate your understanding.
              </p>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-main>
  </div>
</template>

<script>
export default {
  name: "MaintenanceMode",
  props: {
    // Target date/time for maintenance completion
    // Format: ISO string or Date object
    targetDate: {
      type: [String, Date],
      default: () => {
        // Default: 2 hours, 45 minutes, 12 seconds from now
        return new Date(
          Date.now() + 2 * 60 * 60 * 1000 + 45 * 60 * 1000 + 12 * 1000,
        );
      },
    },
    // Title for the maintenance page
    title: {
      type: String,
      default: null,
    },
    // Message/description for the maintenance page
    message: {
      type: String,
      default: null,
    },
    // Optional: support URL (opens in new tab when clicking email link)
    supportUrl: {
      type: String,
      default: "mailto:support@built.africa",
    },
    // Optional: help center URL
    helpCenterUrl: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      progressPercentage: 0,
      initialProgress: 0,
      startTime: Date.now(),
      duration: 0,
      countdownInterval: null,
    };
  },
  computed: {
    countdownItems() {
      return [
        {
          label: "Days",
          value: String(this.days).padStart(2, "0"),
        },
        {
          label: "Hours",
          value: String(this.hours).padStart(2, "0"),
        },
        {
          label: "Minutes",
          value: String(this.minutes).padStart(2, "0"),
        },
        {
          label: "Seconds",
          value: String(this.seconds).padStart(2, "0"),
        },
      ];
    },
    currentYear() {
      return new Date().getFullYear();
    },
    targetDateTime() {
      if (typeof this.targetDate === "string") {
        return new Date(this.targetDate);
      }
      return this.targetDate;
    },
  },
  mounted() {
    // Calculate duration
    this.duration = this.targetDateTime.getTime() - this.startTime;

    // Start countdown
    this.updateCountdown();
    this.countdownInterval = setInterval(() => {
      this.updateCountdown();
    }, 1000);
  },
  beforeDestroy() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  },
  methods: {
    updateCountdown() {
      const now = new Date().getTime();
      const distance = this.targetDateTime.getTime() - now;

      if (distance < 0) {
        // Countdown finished
        this.days = 0;
        this.hours = 0;
        this.minutes = 0;
        this.seconds = 0;
        this.progressPercentage = 100;
        if (this.countdownInterval) {
          clearInterval(this.countdownInterval);
        }
        this.$emit("countdown-finished");
        return;
      }

      // Calculate time units
      this.days = Math.floor(distance / (1000 * 60 * 60 * 24));
      this.hours = Math.floor(
        (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      );
      this.minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      this.seconds = Math.floor((distance % (1000 * 60)) / 1000);

      // Update progress bar
      const elapsed = now - this.startTime;
      this.progressPercentage = Math.min(
        100,
        Math.max(this.initialProgress, (elapsed / this.duration) * 100),
      );
    },
    contactSupport() {
      if (this.supportUrl && this.supportUrl !== "#") {
        window.open(this.supportUrl, "_blank");
      } else {
        this.$emit("contact-support");
      }
    },
    visitHelpCenter() {
      if (this.helpCenterUrl && this.helpCenterUrl !== "#") {
        window.open(this.helpCenterUrl, "_blank");
      } else {
        this.$emit("visit-help-center");
      }
    },
    openLink(type) {
      this.$emit("link-clicked", type);
    },
  },
};
</script>

<style scoped>
.maintenance-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background-color: #f5f5dc;
  border: 1px solid #ffb84d;
  color: #5d4037;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
}

.badge-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #ffb84d;
  display: inline-block;
  flex-shrink: 0;
}

.countdown-wrapper {
  gap: 8px;
}

.countdown-card {
  min-width: 80px;
  padding: 16px 20px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.countdown-number {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
}

.countdown-label {
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  margin-top: 4px;
  color: rgba(255, 255, 255, 0.7);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
}

.countdown-separator {
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1;
  padding-bottom: 24px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
}

.info-card-wrapper {
  width: 100%;
  max-width: 600px;
}

@media (min-width: 960px) {
  .info-card-wrapper {
    width: 50%;
  }
}

@media (max-width: 600px) {
  .countdown-card {
    min-width: 60px;
    padding: 12px 16px;
  }

  .countdown-number {
    font-size: 1.5rem;
  }

  .countdown-separator {
    font-size: 1.2rem;
    padding-bottom: 20px;
  }
}
</style>
